import {
  Component,
  ElementRef,
  OnInit,
  input,
  output,
  signal,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { BaseComponent } from '@app/components/base/base.component';
import { MailshotService } from '../mailshot.service';
import { NotificationService } from '@app/services/notifications.service';
import { ViewChild } from '@angular/core';
import { environment } from '@env/environment.local';

interface FileValidationConfig {
  maxSize: number;
  allowedTypes: string[];
  allowedExtensions: string[];
  errorMessages: {
    sizeExceeded: string;
    invalidType: string;
  };
}

@Component({
  selector: 'app-modal-mailshot',
  templateUrl: './modal-mailshot.component.html',
  styleUrls: ['./modal-mailshot.component.scss'],
})
export class ModalMailshotComponent extends BaseComponent implements OnInit {
  form: FormGroup;

  isLoading: boolean = false;
  isEnabled: boolean = false;
  file: any;

  mailshotId = '';
  selectedOptionsDealers: any[] = [];
  selectedOptionsInternals: any[] = [];
  readonly mailshotRecipientsImprovements =
    environment.mailshotRecipientsImprovements;

  // Configuración específica para archivos de mailshot
  readonly mailshotFileConfig: FileValidationConfig = {
    maxSize: 15 * 1024 * 1024, // 15MB
    allowedTypes: [
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation', // pptx
      'image/jpeg',
      'image/jpg',
      'image/png',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // docx
      'application/msword' // doc
    ],
    allowedExtensions: ['.pdf', '.pptx', '.jpg', '.jpeg', '.png', '.docx', '.doc'],
    errorMessages: {
      sizeExceeded: 'El archivo excede el tamaño máximo de 15MB',
      invalidType: 'Tipo de archivo no permitido. Formatos válidos: PDF, PPTX, JPG, PNG, DOCX'
    }
  };

  @ViewChild('dealersInput') dealersInput: ElementRef<HTMLInputElement>;
  @ViewChild('internalsInput') internalsInput: ElementRef<HTMLInputElement>;
  fileSelectHasError = signal(false);
  internals = input<any[]>([]);
  dealers = input<any[]>([]);
  closed = output<boolean>();

  constructor(
    private fb: FormBuilder,
    private mailshotService: MailshotService,
    private notificationsService: NotificationService
  ) {
    super();
  }

  ngOnInit() {
    this.createForm();
  }

  createForm() {
    this.form = this.fb.group({
      name: ['', [Validators.required]],
      date: [new Date(), [Validators.required]],
      fileName: ['', [Validators.required]],
      mimeType: ['', [Validators.required]],
      fileSize: ['', [Validators.required]],
    });

    this.form.valueChanges.subscribe(() => {
      this.enableSave();
    });
  }

  enableSave() {
    this.isEnabled = this.isEnabled =
      this.form.valid &&
      (this.selectedOptionsDealers.length != 0 ||
        this.selectedOptionsInternals.length != 0) &&
      !!this.file;
  }

  remove(profile: string, type: string): void {
    if (type == 'dealers') {
      const index = this.selectedOptionsDealers.indexOf(profile);

      if (index >= 0) {
        this.selectedOptionsDealers.splice(index, 1);
      }
    }

    if (type == 'internals') {
      const index = this.selectedOptionsInternals.indexOf(profile);

      if (index >= 0) {
        this.selectedOptionsInternals.splice(index, 1);
      }
    }
    this.enableSave();
  }

  selectDealersCustom(updatedSelectedList: any) {
    this.selectedOptionsDealers = updatedSelectedList;
    this.enableSave();
  }

  selectInternalsCustom(updatedSelectedList: any) {
    this.selectedOptionsInternals = updatedSelectedList;
    this.enableSave();
  }

  selectedDealers(event: any): void {
    let isSelectedAll = this.selectedOptionsDealers.some(
      (profile) => profile.description == 'Todos'
    );
    let isSelected = this.selectedOptionsDealers.some(
      (profile) => profile.id == event.option.value.id
    );

    if (isSelectedAll && event.option.viewValue != 'Todos') {
      this.selectedOptionsDealers = [];
      this.selectedOptionsDealers.push(event.option.value);
    } else if (!isSelectedAll && event.option.viewValue == 'Todos') {
      this.selectedOptionsDealers = [];
      this.selectedOptionsDealers.push({ description: 'Todos' });
    } else if (
      !isSelectedAll &&
      event.option.viewValue != 'Todos' &&
      !isSelected
    ) {
      this.selectedOptionsDealers.push(event.option.value);
    }
    this.dealersInput.nativeElement.value = '';
    this.enableSave();
  }

  selectedInernals(event: any): void {
    let isSelectedAll = this.selectedOptionsInternals.some(
      (profile) => profile.description == 'Todos'
    );
    let isSelected = this.selectedOptionsInternals.some(
      (profile) => profile.id == event.option.value.id
    );

    if (isSelectedAll && event.option.viewValue != 'Todos') {
      this.selectedOptionsInternals = [];
      this.selectedOptionsInternals.push(event.option.value);
    } else if (!isSelectedAll && event.option.viewValue == 'Todos') {
      this.selectedOptionsInternals = [];
      this.selectedOptionsInternals.push({ description: 'Todos' });
    } else if (
      !isSelectedAll &&
      event.option.viewValue != 'Todos' &&
      !isSelected
    ) {
      this.selectedOptionsInternals.push(event.option.value);
    }
    this.internalsInput.nativeElement.value = '';
    this.enableSave();
  }

  onClose(): void {
    this.closed.emit(true);
  }

  getSelectedProfiles() {
    let dealers = this.selectedOptionsDealers;
    let internals = this.selectedOptionsInternals;

    if (dealers.length == 1 && dealers[0].description == 'Todos') {
      dealers = this.dealers().map((i: { profileCode: any; userType: any }) => {
        return { profileCode: i.profileCode, userType: i.userType };
      });
    } else if (dealers) {
      dealers = this.selectedOptionsDealers.map((i) => {
        return { profileCode: i.profileCode, userType: i.userType };
      });
    }

    if (internals.length == 1 && internals[0].description == 'Todos') {
      internals = this.internals().map(
        (i: { id: any; profileCode: any; userType: any }) => {
          return {
            ...(this.mailshotRecipientsImprovements
              ? { profileId: i.id }
              : {}),
            profileCode: i.profileCode,
            userType: i.userType,
          };
        }
      );
    } else if (internals) {
      internals = this.selectedOptionsInternals.map((i) => {
        return {
          ...(this.mailshotRecipientsImprovements
            ? { profileId: i.id }
            : {}),
          profileCode: i.profileCode,
          userType: i.userType,
        };
      });
    }

    let result: any[] = [];
    if (dealers && internals) {
      result = dealers.concat(internals);
    } else if (dealers && !internals) {
      result = dealers;
    } else if (!dealers && internals) {
      result = internals;
    }
    const unique = [
      ...new Map(
        result.map((item) => [
          `${item.profileCode}-${item.userType}${
            this.mailshotRecipientsImprovements
              ? `-${item.profileId ?? ''}`
              : ''
          }`,
          item,
        ])
      ).values(),
    ];
    return unique;
  }

  getModel() {
    let model = {
      name: this.form.controls['name'].value,
      date: this.form.controls['date'].value,
      profiles: this.getSelectedProfiles(),
      mimeType: this.form.controls['mimeType'].value,
      fileSize: this.form.controls['fileSize'].value,
      profileModule: 'ADMINISTRATOR',
    };
    return model;
  }

  save() {
    this.isLoading = true;
    let data = this.getModel();
    if (this.validForm(this.form)) {
      this.mailshotService.createMailshot(data).subscribe({
        next: (r) => {
          this.isLoading = false;
          this.mailshotId = r.data;
          this.createAttachment();
        },
        error: (error) => {
          this.isLoading = false;
          this.notificationsService.showMessage(
            ['Ha ocurrido un error. Vuelva a intentarlo.'],
            'error'
          );
        },
      });
    } else {
      this.isLoading = false;
      this.notificationsService.showMessage(
        ['Revisa los campos obligatorios antes de continuar.'],
        'error'
      );
    }
  }

  createAttachment() {
    this.isLoading = true;

    let file = this.createFormData();

    this.mailshotService.createAttachment(file).subscribe({
      next: (r: any) => {
        this.sendNotifications();
        this.isLoading = false;
        this.notificationsService.showMessage(
          ['La circular se creado correctamente.'],
          'success'
        );
        setTimeout(() => {
          this.notificationsService.clearMessages();
          this.onClose();
        }, 2000);
      },
      error: (error: any) => {
        this.isLoading = false;
        this.deleteMailshot();
        this.notificationsService.showMessage(
          ['Ha ocurrido un error al subir el archivo. Vuelva a intentarlo.'],
          'error'
        );
        setTimeout(() => {
          this.notificationsService.clearMessages();
        }, 6000);
      },
    });
  }

  deleteMailshot() {
    this.mailshotService.deleteMailshot(this.mailshotId).subscribe({
      next: (response: any) => {},
      error: (error: any) => {
        this.notificationsService.showMessage(
          [
            'Ha ocurrido un error al eliminar la circular. Vuelva a intentarlo.',
          ],
          'error'
        );
      },
    });
  }

  sendNotifications() {
    let data = {
      fileSize: this.file.size,
      profiles: this.getSelectedProfiles(),
    };

    this.mailshotService.sendNotifications(data, this.mailshotId).subscribe({
      next: (next: any) => {},
      error: (error: any) => {
        this.notificationsService.showMessage(
          [
            'Ha ocurrido un error al enviar notificaciones. Vuelva a intentarlo.',
          ],
          'error'
        );
      },
    });
  }

  onRemoveFile() {
    this.form.controls['mimeType'].setValue('');
    this.form.controls['fileSize'].setValue('');
    this.form.controls['fileName'].setValue('');
    this.fileSelectHasError.set(true);
    this.file = null;
    this.enableSave();
  }

  onFileSelected(event: any) {
    const file: File = event.target.files[0];
    const input = event.target as HTMLInputElement;

    let maxFileSize = 10 * 1024 * 1024; // 10 MB en bytes

    if (file) {
      const isMaxFIleValid = file.size <= maxFileSize;
      const isFileTypeValid = [
        'application/pdf',
        'image/jpeg',
        'image/png',
      ].includes(file.type);
      if (isMaxFIleValid && isFileTypeValid) {
        this.form.controls['mimeType'].setValue(file.type);
        this.form.controls['fileSize'].setValue(file.size);
        this.form.controls['fileName'].setValue(file.name);
        this.file = file;
        this.fileSelectHasError.set(false);
        this.enableSave();
      } else {
        input.value = '';
        const errors = [];
        if (!isMaxFIleValid)
          errors.push('El archivo supera el tamaño máximo permitido (10 MB).');
        if (!isFileTypeValid)
          errors.push(
            'El formato del archivo no es un formato valido (jpg,png,pdf)'
          );
        this.notificationsService.showMessage(errors, 'error');
        this.fileSelectHasError.set(true);
        setTimeout(() => {
          this.notificationsService.clearMessages();
        }, 5000);
      }
    }
  }

  createFormData() {
    let formData = new FormData();
    formData.append('originalName', this.file.name);
    formData.append('mimeType', this.file.type);
    formData.append('elementId', this.mailshotId);
    formData.append('typeFile', 'MAIL_SHOT');
    formData.append('fileSize', this.file.size);
    formData.append('file', this.file);
    return formData;
  }
}
